'use client';

import { useParams } from 'next/navigation';
import React, { useState, useEffect } from 'react';
import { format as dateFormat } from 'date-fns';
import {
  useGridApiRef,
  DataGridPremium,
  GridRowSelectionModel,
  GridColumnMenuProps,
  GridColumnMenu,
} from '@mui/x-data-grid-premium';
import { LOCATION } from 'src/hooks/filters/constants';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import useLocalStorage from 'src/hooks/system/useLocalStorage';
import { useGetEducationNetworkQuery } from 'src/api/redux/education/educationApi';
import {
  TableSettings,
  educationTableColumns,
  EncountersToolbar,
  TableCheckbox,
  TableFooter,
  CircularProgress,
} from 'src/components';
import {
  EducationSelector,
  EducationType,
} from 'src/modules/Profile/components';
import { useProspectSelection } from 'src/context/prospects';
import { useLogging } from 'src/context/logging';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { kebabCase } from 'src/utils/kebabCase/kebabCase';
import { useDebounce } from 'use-debounce';

const EducationTable = () => {
  const log = useLogging();
  const {
    state: [, setNewProspects],
    selectedRows: [rowsAreSelected],
  } = useProspectSelection();

  const apiRef = useGridApiRef();
  const { entityDetails } = useProfile();
  const auth = useAuthorization();

  const { id } = useParams<{ id: string }>();

  // State for the current selection
  const [selectedOption, setSelectedOption] =
    useState<EducationType>('Residency');
  const [search, setSearch] = useState('');
  const [debounceSearch] = useDebounce(search, 500);
  const [isExporting, _setIsExporting] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const [sort, setSort] = useState({
    sort: 'name',
    sort_id: null,
    sort_field: null,
    order: 'asc',
  });

  const {
    data: resultsData,
    isLoading,
    isFetching,
  } = useGetEducationNetworkQuery(
    {
      id,
      education_type: selectedOption,
      page: paginationModel?.page + 1 || 0,
      pageSize: paginationModel?.pageSize || 50,
      ...sort,
      q: debounceSearch,
    },
    { skip: !id }
  );

  // toggle state to clear selected rows
  useEffect(() => {
    setSelectionModel(() => []);
  }, [rowsAreSelected]);

  // update selected rows when DataGrid selection model changes
  useEffect(() => {
    if (!apiRef.current?.getSelectedRows) return;
    setNewProspects(Array.from(apiRef.current.getSelectedRows().values()));
    return () => setNewProspects([]);
  }, [apiRef, selectionModel, setNewProspects]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    'newEducationTable',
    {
      name: true,
      crm_links: true,
      specialty: true,
      location: true,
      city: true,
      focus: true,
      graduation_year: true,
      institution: true,
      provider: true,
      state: true,
      tags: true,
      provider_id: false,
      address: false,
      zip_code: false,
      phone: false,
      email: false,
    }
  );

  useEffect(() => {
    if (!educationTableColumns.length) return;

    setColumnVisibilityModel((columnVisibilityState) => ({
      ...columnVisibilityState,
      ...serializeColumnVisibility(columnVisibilityState),
    }));

    function serializeColumnVisibility(currentVisibility) {
      const visibilityMap = {};

      for (const col of educationTableColumns) {
        const field = col.field;
        const isVisible = !!currentVisibility[field];

        visibilityMap[field] = isVisible;
      }

      return visibilityMap;
    }
  }, [setColumnVisibilityModel]);

  // This handles normal exports, i.e. not server side exports. Notes for later
  const handleExportList = (format) => {
    if (!format) return;
    const fileName = `${kebabCase(
      entityDetails?.name
    )}_${`education_MedScout`}_${dateFormat(Date.now(), 'M_d_yyyy')}`;
    const api = apiRef.current;
    if (format === 'csv') {
      api.exportDataAsCsv({ fileName });
    } else if (format === 'xlsx') {
      api.exportDataAsExcel({ fileName });
    } else {
      log.exception('Invalid export type', {
        tags: {
          source: 'education',
          format,
          context: `${entityDetails?.provider_id} - ${entityDetails?.name}`,
        },
      });
    }
    log.event('Export', {
      type: 'prospects',
      source: 'education',
      format: format?.toUpperCase(),
      count: resultsData?.results?.length,
      context: entityDetails?.provider_id,
    });
  };

  const handleSortChange = (model) => {
    let sort = 'name';
    let sort_id = null;
    const sort_field = null;
    let order = 'desc';

    if (model?.length) {
      const { field, sort: sortDirection } = model[0];
      order = sortDirection || 'desc';
      sort = isNaN(Number(field)) && field !== 'volume' ? field : 'volume';
      sort_id = field;
    }

    setSort({
      sort: sort,
      sort_id: sort_id,
      sort_field: sort_field,
      order: order,
    });
  };

  const handleSearch = (value) => {
    setSearch(value);
    // ensure we are on the 1st page for searches
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handlePageChange = (e, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (e) => {
    setPaginationModel((prev) => ({
      ...prev,
      pageSize: parseInt(e.target.value, 10),
      page: 0,
    }));
  };

  const userCanExport = auth.hasPermission('export.hcp_education.all');

  return (
    <div
      data-testid="education-table"
      role="region"
      aria-label="Education network table"
      className="relative flex flex-col p-0 bottom-0 h-full w-full min-w-0
      overflow-hidden border border-netural-200 rounded-lg bg-white"
    >
      <div className="px-4 pt-6">
        <EducationSelector
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
      </div>
      <div
        className="w-full h-full max-h-[calc(100vh-25rem)] overflow-hidden flex
     flex-col"
      >
        <DataGridPremium
          apiRef={apiRef}
          loading={isLoading || isFetching}
          rowCount={resultsData?.count || 0}
          rows={resultsData?.results || []}
          columns={educationTableColumns}
          hideFooter={true}
          sx={{
            // Base table styling
            border: 'none',
            borderRadius: 0,
            overflow: 'hidden',
            height: '100%',
            flex: 1,

            // Fix for pinned columns alignment
            '& .MuiDataGrid-columnHeaders': {
              position: 'relative', // Ensure headers have a positioning context
              paddingRight: '0px',
              '& .MuiDataGrid-columnSeparator': {
                color: 'none',
              },
            },

            // Pinned column headers (v7: native sticky, no position overrides)
            '& .MuiDataGrid-columnHeader--pinnedRight': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
            },

            // Pinned cells (v7: native sticky, no position overrides)
            '& .MuiDataGrid-cell--pinnedRight': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
            },

            // Virtual scroller
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto', // Keep default overflow behavior
              overflowX: 'auto', // Allow horizontal scrolling
              minHeight: '300px', // Ensure minimum height even when empty
            },

            // Empty state styling
            '& .MuiDataGrid-overlay': {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },

            // Primary columns hover effect
            '& .MuiDataGrid-row:hover, & .Mui-hovered': {
              backgroundColor: 'rgba(204, 204, 194, 0.13)',
              '&.Mui-hovered': {
                backgroundColor: 'rgba(204, 204, 194, 0.13)',
              },
            },

            // Secondary columns - base styling
            '& .secondary-column-header, & .secondary-column-cell': {
              background: 'var(--secondary-outlined-ActiveBg, #D6F6F1)',
              borderRight: '#00C6A9 1px solid',
            },

            // Secondary column headers specific styling
            '& .secondary-column-header': {
              borderBottom: '#00C6A9 1px solid !important', // Restored 2px with !important to ensure visibility
              boxShadow: 'none',
            },

            // Override any conflicting styles from MUI
            '& .MuiDataGrid-columnHeaders .secondary-column-header, & .MuiDataGrid-columnHeadersInner .secondary-column-header':
              {
                borderBottom: '#00C6A9 1px solid !important', // Ensure this is not overridden
              },

            // Add left border to the first pinned column header and cell
            '& .MuiDataGrid-columnHeader--pinnedRight:first-of-type, & .MuiDataGrid-cell--pinnedRight:first-of-type':
              {
                borderLeft: '#00C6A9 1px solid',
              },

            // Column separators in secondary section
            '& .secondary-column-header .MuiDataGrid-columnSeparator': {
              color: '#00C6A9',
            },

            // Hover effect for secondary columns
            '& .MuiDataGrid-row:hover .secondary-column-cell, & .Mui-hovered .secondary-column-cell':
              {
                backgroundColor: '#D6F6F1',
              },

            // Hover effect for pinned cells
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight, & .Mui-hovered .MuiDataGrid-cell--pinnedRight':
              {
                backgroundColor: '#D6F6F1',
              },
          }}
          initialState={{
            columns: { columnVisibilityModel },
            pagination: { paginationModel: { page: 0, pageSize: 50 } },
          }}
          slots={{
            toolbar: EncountersToolbar,
            baseCheckbox: TableCheckbox,
            columnMenu: CustomColumnMenu,
            loadingOverlay: () => (
              <div
                data-testid="education-table-loading"
                role="status"
                aria-label="Loading education data"
                className="flex items-center justify-center h-full backdrop-blur-sm"
              >
                <CircularProgress size="sm" color="neutral" />
              </div>
            ),
            ...TableSettings.slots,
          }}
          slotProps={{
            columnsManagement: {
              disableShowHideToggle: true,
            },
            toolbar: {
              location: LOCATION.EDUCATION,
              onSearch: handleSearch,
              // Export button props
              onExport: handleExportList,
              isExporting: isExporting,
              // Column button props
              columns: educationTableColumns,
              columnVisibilityModel: columnVisibilityModel,
              setColumnVisibility: setColumnVisibilityModel,
              // Feature flags
              showExportButton: userCanExport,
              showDeleteButton: true,
            },
          }}
          // Pagination
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          // Sorting
          sortingMode="server"
          onSortModelChange={handleSortChange}
          // Column visibility
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          // Rows
          rowHeight={75}
          onRowSelectionModelChange={setSelectionModel}
          rowSelectionModel={selectionModel}
          getRowId={(row) => row.id}
        />
        <TableFooter
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          rowCount={resultsData?.count || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

const CustomColumnMenu = (props: GridColumnMenuProps) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuFilterItem: null,
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  );
};

export default EducationTable;
